<template>
  <main class="release">
    <div class="release__stack">
      <DsProgramHero
        tag="Liberación y cancelación"
        :image="heroImage"
        :tablet-image="heroImage"
        :mobile-image="heroMobileImage"
        image-alt="Propietario revisando su teléfono"
        title-id="release-title"
      >
        <template #title>
          <h1><span>Liberación y cancelación</span> de hipoteca.</h1>
        </template>
        <p>¡Libérate de la hipoteca y toma las llaves de tu patrimonio! Asegura oficialmente tu propiedad ante el Registro Público de la Propiedad (RPP).</p>
      </DsProgramHero>

      <section class="release__importance" aria-labelledby="release-importance-title">
        <h2 id="release-importance-title">¿Por qué es importante <span>este proceso?</span></h2>
        <div class="release__feature-grid">
          <article v-for="feature in features" :key="feature.text" class="release__feature" :class="{ 'release__feature--wide': feature.wide }">
            <picture>
              <source media="(max-width: 767px)" :srcset="feature.mobileIcon">
              <img :src="feature.icon" alt="" aria-hidden="true">
            </picture>
            <p v-html="feature.text" />
          </article>
        </div>
      </section>

      <section class="release__help" aria-labelledby="release-help-title">
        <div class="release__help-title">
          <picture>
            <source media="(max-width: 767px)" :srcset="helpMobileIcon">
            <img :src="helpIcon" alt="" aria-hidden="true">
          </picture>
          <h2 id="release-help-title">¿Cómo te ayuda <span>este proceso?</span></h2>
        </div>
        <p>Te brinda la seguridad jurídica necesaria para ostentarte como dueño absoluto de tu inmueble, eliminando el gravamen sobre tu propiedad y permitiéndote disfrutar plenamente de tu logro.</p>
      </section>

      <section class="release__process" aria-labelledby="release-process-title">
        <h2 id="release-process-title">¿Cómo es el <span>proceso?</span></h2>
        <div class="release__steps">
          <article v-for="(step, index) in steps" :key="step.title" class="release__step">
            <header>
              <span>{{ index + 1 }}</span>
              <h3>{{ step.title }}</h3>
            </header>
            <p>{{ step.description }}</p>
          </article>
        </div>
      </section>

      <section class="release__documentation" aria-labelledby="release-documentation-title">
        <div class="release__documentation-content">
          <div>
            <h2 id="release-documentation-title"><span>Lo que necesitarás</span> durante tu proceso</h2>
            <p>Tu camino está dividido en etapas claras.<br>Aquí puedes entender en cuál estás y qué sigue:</p>
          </div>
          <ul>
            <li v-for="item in documents" :key="item.text">
              <picture>
                <source media="(max-width: 767px)" :srcset="item.mobileIcon">
                <img :src="item.icon" alt="" aria-hidden="true">
              </picture>
              <span>{{ item.text }}</span>
            </li>
          </ul>
        </div>
        <picture class="release__documentation-media">
          <source media="(max-width: 767px)" :srcset="documentationMobileImage">
          <img class="release__documentation-image" :src="documentationImage" alt="Propietario consultando su teléfono">
        </picture>
      </section>

      <section class="release__recommendations" aria-labelledby="release-recommendations-title">
        <h2 id="release-recommendations-title">Recomendaciones importantes</h2>
        <ul>
          <li>La carta de liberación tiene una vigencia de 90 días. Asegúrate de realizar el trámite dentro de este periodo.</li>
          <li>Mantente atento a los 30 días naturales que requiere la emisión de tu carta de instrucciones.</li>
        </ul>
      </section>
    </div>

    <DsContactHelp />
  </main>
</template>

<script setup lang="ts">
import heroImage from '../../../assets/liberacion/hero.png'
import heroMobileImage from '../../../assets/liberacion/hero-mobile.png'
import documentationImage from '../../../assets/liberacion/documentacion.png'
import documentationMobileImage from '../../../assets/liberacion/documentacion-mobile.png'
import validityIcon from '../../../assets/liberacion/icon-vigencia.svg'
import validityMobileIcon from '../../../assets/liberacion/icon-vigencia-mobile.svg'
import issuanceIcon from '../../../assets/liberacion/icon-emision.svg'
import issuanceMobileIcon from '../../../assets/liberacion/icon-emision-mobile.svg'
import propertyIcon from '../../../assets/liberacion/icon-patrimonio.svg'
import propertyMobileIcon from '../../../assets/liberacion/icon-patrimonio-mobile.svg'
import helpIcon from '../../../assets/liberacion/icon-ayuda.svg'
import helpMobileIcon from '../../../assets/liberacion/icon-ayuda-mobile.svg'
import quoteIcon from '../../../assets/liberacion/icon-cotizacion.svg'
import quoteMobileIcon from '../../../assets/liberacion/icon-cotizacion-mobile.svg'
import notaryIcon from '../../../assets/liberacion/icon-notaria.svg'
import notaryMobileIcon from '../../../assets/liberacion/icon-notaria-mobile.svg'
import letterIcon from '../../../assets/liberacion/icon-carta.svg'
import letterMobileIcon from '../../../assets/liberacion/icon-carta-mobile.svg'
import DsContactHelp from '../components/DsContactHelp.vue'
import DsProgramHero from '../components/DsProgramHero.vue'

const features = [
  { icon: propertyIcon, mobileIcon: propertyMobileIcon, wide: true, text: '<strong>Al adquirir un activo</strong><br>(casa, oficina o local), el inmueble debe quedar registrado en el RPP como garantía de cumplimiento. La liberación es el paso final para que el inmueble se convierta oficialmente en tu patrimonio para toda la vida.' },
  { icon: validityIcon, mobileIcon: validityMobileIcon, text: 'La carta de instrucciones de cancelación de hipoteca tiene una <strong>vigencia de 90 días.</strong>' },
  { icon: issuanceIcon, mobileIcon: issuanceMobileIcon, text: 'La primera expedición de la carta es gratuita y se emite en un tiempo aproximado de <strong>30 días naturales.</strong>' }
]

const steps = [
  { title: 'Cotización en Notaría', description: 'Acude a la notaría de tu preferencia para solicitar una cotización del costo del trámite. (Consulta con nosotros si tenemos opciones recomendadas con convenio en tu localidad).' },
  { title: 'Gestión de Datos', description: 'Proporciónanos el número de contacto de la notaría, el nombre del notario y la ubicación del inmueble.' },
  { title: 'Paso 3', description: 'Te haremos entrega de la “Carta de instrucciones de cancelación de hipoteca”, la cual deberás presentar en la notaría elegida.' },
  { title: 'Paso 4', description: 'Una vez entregada la carta de liberación, la notaría se encargará de concluir el proceso ante el RPP.' }
]

const documents = [
  { icon: quoteIcon, mobileIcon: quoteMobileIcon, text: 'Cotización de gastos notariales' },
  { icon: notaryIcon, mobileIcon: notaryMobileIcon, text: 'Datos de la notaría seleccionada (nombre del notario, contacto y ubicación)' },
  { icon: letterIcon, mobileIcon: letterMobileIcon, text: 'Carta de instrucciones de cancelación de hipoteca (emitida por HIR Casa)' }
]
</script>

<style scoped>
.release { min-height: calc(100vh - 68px); overflow-x: hidden; background: var(--ds-grey-100); }
.release__stack { display: flex; flex-direction: column; gap: 40px; width: 100%; max-width: 1010px; margin: 0 auto; padding: 40px 24px 48px; box-sizing: border-box; }
.release h1, .release h2 { margin: 0; color: var(--ds-purple-900); font-family: var(--ds-font-family-bebas); font-weight: 700; }
.release h1, .release h2 { font-size: 40px; line-height: 44px; }
.release h1 span, .release h2 span { color: var(--ds-purple-400); }
.release p { margin: 0; color: var(--ds-text-primary); font-size: 16px; line-height: 24px; }
.release__importance, .release__process { display: flex; flex-direction: column; gap: 32px; }
.release__feature-grid { display: grid; gap: 16px; }
.release__feature { display: flex; flex-direction: column; gap: 16px; padding: 24px 16px; overflow: hidden; border-radius: 24px; background: var(--ds-grey-50); }
.release__feature picture, .release__help-title picture, .release__documentation li picture { display: block; flex: 0 0 40px; width: 40px; height: 40px; }
.release__feature img, .release__help-title img, .release__documentation li img { display: block; width: 40px; height: 40px; object-fit: contain; }
.release__help { display: flex; flex-direction: column; gap: 24px; padding: 24px 16px; overflow: hidden; border-radius: 24px; background: var(--ds-grey-50); }
.release__help-title { display: flex; align-items: center; gap: 8px; }
.release__steps { display: grid; gap: 16px; }
.release__step { display: flex; flex-direction: column; gap: 16px; padding: 24px; border: 1px solid var(--ds-purple-200); border-radius: 8px; background: #fbfbfb; }
.release__step header { display: flex; align-items: center; gap: 16px; min-height: 64px; padding-bottom: 16px; border-bottom: 1px solid var(--ds-grey-300); }
.release__step header span { display: grid; flex: 0 0 48px; width: 48px; height: 48px; place-items: center; border-radius: 50%; background: var(--ds-purple-50); color: var(--ds-purple-900); font-family: var(--ds-font-family-bebas); font-size: 24px; }
.release__step h3 { margin: 0; color: var(--ds-purple-900); font-size: 16px; line-height: 20px; font-weight: 600; }
.release__documentation { display: flex; flex-direction: column; gap: 24px; }
.release__documentation-content { display: flex; flex-direction: column; gap: 40px; }
.release__documentation-content > div { display: flex; flex-direction: column; gap: 16px; }
.release__documentation ul { display: flex; flex-direction: column; gap: 32px; margin: 0; padding: 0; list-style: none; }
.release__documentation li { display: flex; align-items: center; gap: 8px; color: var(--ds-text-primary); font-size: 16px; line-height: 24px; }
.release__documentation-media { display: block; width: 100%; height: 608px; overflow: hidden; border-radius: 24px; }
.release__documentation-image { display: block; width: 100%; height: 100%; object-fit: cover; }
.release__recommendations { display: flex; flex-direction: column; gap: 24px; }
.release__recommendations h2 { font-size: 32px; line-height: 40px; }
.release__recommendations ul { display: flex; flex-direction: column; gap: 24px; margin: 0; padding-left: 20px; }
.release__recommendations li { color: var(--ds-text-primary); font-size: 16px; line-height: 24px; }

@media (max-width: 767px) {
  .release__stack { gap: 32px; padding: 32px 16px 48px; }
  .release :deep(.program-hero) { gap: 32px; }
  .release :deep(.program-hero__content) { gap: 32px; }
  .release :deep(.program-hero__media) { height: 328px; aspect-ratio: auto; }
  .release__importance > h2 { margin: 0 16px; }
  .release__feature-grid { gap: 32px; }
  .release__help { gap: 8px; }
  .release__steps { gap: 24px; }
  .release__step header { height: 88px; min-height: 88px; padding-bottom: 0; box-sizing: border-box; }
  .release__step > p { font-family: Inter, sans-serif; line-height: 19px; }
  .release__documentation-content, .release__documentation-content > div { gap: 24px; }
}

@media (min-width: 768px) {
  .release__feature-grid, .release__steps { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
  .release__feature-grid { gap: 32px; }
  .release__feature:nth-child(2) { order: 1; }
  .release__feature:nth-child(3) { order: 2; }
  .release__feature--wide { grid-column: 1 / -1; order: 3; }
  .release__documentation { display: grid; grid-template-columns: minmax(0, 1fr) 370px; align-items: stretch; }
  .release__documentation-media { height: 100%; min-height: 384px; }
}
</style>
